import { computed } from "vue";
import type { BungeecordProfileNamespace } from "../graphql";
import { commonMenuItems } from "./common";
import { defaultProfile } from "./index";
import type { ConfigSection, GameProfile, Menu, ProfileCtx } from "./types";

export type BungeecordServer = {
  motd: string;
  restricted: boolean;
};

export interface BungeecordGpServer extends BungeecordServer {
  name: string;
  serviceId: number;
}

export interface BungeecordExternalServer extends BungeecordGpServer {
  serverName: string;
  address: string;
  port: number;
}

export interface Bungeecord {
  name: string;
  gpServers?: BungeecordGpServer[];
}

function parseJsonArray(jsonArray: string[]) {
  return jsonArray
    .map((jsonStr) => {
      try {
        return JSON.parse(jsonStr);
      } catch (e) {
        console.error("Failed to parse JSON string:", jsonStr, e);
        return null;
      }
    })
    .filter((item) => item !== null);
}

function initializeGpServersList(gpServers: string[]) {
  return parseJsonArray(gpServers);
}

function createConfigSections(): ConfigSection[] {
  return [
    { component: "BungeecordJavaSelector" },
    { component: "ConfSection", props: { name: "basic" } },
    {
      component: "BungeecordServersList",
      props: {
        name: "bungeecord_servers",
      },
    },
  ];
}

const createMenu = (): Menu => {
  const menu = defaultProfile().menu;
  menu[0].items.push(commonMenuItems.logs, commonMenuItems.console);
  return menu;
};

// BungeeCord's JLine console writes each log line as multiple small I/O chunks (one per
// flush boundary), which asyncio reads as separate pipe chunks — each becoming its own
// gRPC message and "line" in our console. We need to rejoin these mid-word fragments.
//
// Strategy:
// - Lines starting with a timestamp (HH:MM:SS) begin a new log entry.
// - Lines without a leading timestamp are continuations of the previous entry.
// - Buffer the current entry and emit it when the next new-timestamp line arrives.
// - Orphaned SGR fragments ([digits...m without ESC) are stripped so ansi_up renders
//   intact \x1b[...m sequences as colors without showing visible garbage.

// Orphaned SGR: `[digits...m` NOT preceded by ESC. Negative lookbehind keeps intact sequences.
const ORPHANED_SGR = /(?<!\x1b)\[[\d;]*m/g;
// Complete CSI sequences (with ESC), used only for content-checking.
const COMPLETE_CSI = /\x1b\[[\x30-\x3f]*[\x20-\x2f]*[\x40-\x7e]/g;

const makeConsoleFilter = () => {
  let pending: string | null = null;

  const filter = (line: string): string | null => {
    const withoutCR = line.replace(/\r/g, "");
    if (!withoutCR.trim()) return null;

    const display = withoutCR.replace(ORPHANED_SGR, "");
    const content = display.replace(COMPLETE_CSI, "").trim();

    // Pure ANSI (e.g. trailing reset): append to pending but don't emit — the flush timeout handles that.
    if (!content) {
      if (pending !== null && display.trim()) pending = pending + display;
      return null;
    }
    if (content === "Skipped some lines of output") return null;

    // Strip trailing `>` prompt artifact (BungeeCord's JLine prompt bleeds into output).
    // Use trimStart only — trailing spaces on chunk boundaries are needed for word joins.
    const cleaned = display.replace(/ ?>$/, "").trimStart();
    if (!cleaned.trim()) return null;

    // New timestamp-started line: emit previous pending, start buffering this line.
    if (/^\d{2}:\d{2}:\d{2}/.test(content)) {
      const result = pending;
      pending = cleaned;
      return result;
    }

    // Continuation fragment: append to pending.
    pending = pending !== null ? pending + cleaned : cleaned;
    return null;
  };

  filter.reset = () => { pending = null; };
  filter.flush = () => { if (pending !== null) { const r = pending; pending = null; return r; } return null; };

  return filter;
};

const consoleFilter = makeConsoleFilter();

// BungeeCord sometimes delivers two log lines in a single I/O chunk without a \n separator
// (e.g. "...by md_5.23:10:00 [INFO]..."). Insert \n before any embedded timestamp so the
// \n-splitter in service.ts sees them as separate lines.
const EMBEDDED_TIMESTAMP = /(?<=.)\d{2}:\d{2}:\d{2} \[/g;
const consoleMessagePreprocessor = (msg: string): string => msg.replace(EMBEDDED_TIMESTAMP, "\n$&");

const consoleCommands = [
  "alert",
  "alertraw",
  "bungee",
  "end",
  "find",
  "glist",
  "greload",
  "ip",
  "perms",
  "send",
  "server",
];

function configSections(ctx: ProfileCtx): ConfigSection[] {
  const profile = computed(() => ctx.profile as BungeecordProfileNamespace);

  const gpServersParsed = initializeGpServersList(profile.value.gpServers);

  profile.value.publicConfigs.virtual["gp_servers"] = gpServersParsed;

  if (profile.value && profile.value.gpServers) {
    return createConfigSections(gpServersParsed);
  } else {
    return createConfigSections([]);
  }
}

export default (ctx: ProfileCtx): Partial<GameProfile> => ({
  menu: createMenu(),
  hasActivePlayers: false,
  configSections: configSections(ctx),
  consoleCommands,
  consoleFilter,
  consoleMessagePreprocessor,
});
